/**
 * RequireAdmin — route guard that redirects non-admin users away from admin-only pages.
 *
 * The backend is the authoritative check (routes return 403); this component is UX
 * polish so users aren't dropped into an admin screen they can't use.
 */

import { Navigate, useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "@/hooks/use-permissions";
import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

interface RequireAdminProps {
  children: ReactNode;
  /**
   * How to handle non-admins:
   * - "redirect" (default): silently bounce to chat (existing behaviour).
   * - "screen": render an explicit "not authorized" page (for deep-linked
   *   admin pages like the audit log, so a pasted URL gets a clear message).
   */
  fallback?: "redirect" | "screen";
}

export function RequireAdmin({ children, fallback = "redirect" }: RequireAdminProps) {
  const { isAdmin } = usePermissions();
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();

  if (!isAdmin) {
    if (fallback === "screen") {
      return <UnauthorizedScreen onBack={() => navigate(appId ? `/app/${appId}/chat` : "/")} />;
    }
    const to = appId ? `/app/${appId}/chat` : "/";
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
}

/** Full-page "not authorized" state for deep-linked admin-only routes. */
function UnauthorizedScreen({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-full bg-destructive/10">
          <ShieldAlert className="h-7 w-7 text-destructive" />
        </div>
        <h1 className="text-xl font-semibold text-foreground">
          You're not authorized to view this page
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This area is restricted to workspace admins. If you need access, ask a
          workspace admin to grant it.
        </p>
        <Button className="mt-6" onClick={onBack}>
          Back to chat
        </Button>
      </div>
    </div>
  );
}
